import { useEffect, useRef } from 'react';

/**
 * Helper to check if any Mantine modal or drawer is currently open in the DOM
 */
const isAnyModalOpen = (): boolean => {
  return !!(
    document.querySelector('.mantine-Modal-root') ||
    document.querySelector('.mantine-Drawer-root') ||
    document.querySelector('[data-mantine-modal]') ||
    document.querySelector('[role="dialog"]')
  );
};

/**
 * Hook to dynamically control viewport zoom settings based on the current view
 * @param disableZoom - Whether to disable zoom for this view
 * @param override - Optional override to temporarily enable zoom (e.g., when modal is open)
 */
export const useViewportControl = (disableZoom: boolean, override?: boolean) => {
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) return;

    // Save original content
    const originalContent = viewport.getAttribute('content');

    const updateViewport = () => {
      // Check both the override prop AND if any modal/drawer is open in DOM
      const modalOpenInDOM = isAnyModalOpen();
      const shouldDisableZoom = override === true || modalOpenInDOM ? false : disableZoom;

      if (shouldDisableZoom) {
        // Disable zoom for this view (like map view without modals)
        viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0');
        console.log('Viewport: Zoom disabled');
      } else {
        // Enable zoom for this view (like chatbot or when modal is open)
        viewport.setAttribute('content', 'width=device-width, initial-scale=1');
        console.log('Viewport: Zoom enabled' + (modalOpenInDOM ? ' (modal detected)' : override ? ' (override active)' : ''));
      }
    };

    // Initial update
    updateViewport();

    // Watch for DOM changes to detect modal/drawer open/close
    observerRef.current = new MutationObserver((mutations) => {
      // Check if any mutation added or removed modal-related elements
      const shouldUpdate = mutations.some((mutation) => {
        if (mutation.type === 'childList') {
          const addedNodes = Array.from(mutation.addedNodes);
          const removedNodes = Array.from(mutation.removedNodes);

          const isModalRelated = (node: Node): boolean => {
            if (node.nodeType !== Node.ELEMENT_NODE) return false;
            const el = node as Element;
            return !!(
              el.classList?.contains('mantine-Modal-root') ||
              el.classList?.contains('mantine-Drawer-root') ||
              el.hasAttribute?.('data-mantine-modal') ||
              el.getAttribute?.('role') === 'dialog' ||
              el.querySelector?.('.mantine-Modal-root, .mantine-Drawer-root, [role="dialog"]')
            );
          };

          return addedNodes.some(isModalRelated) || removedNodes.some(isModalRelated);
        }
        return false;
      });

      if (shouldUpdate) {
        updateViewport();
      }
    });

    // Observe the entire document for modal additions/removals
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Cleanup: restore original on unmount
    return () => {
      observerRef.current?.disconnect();
      if (originalContent) {
        viewport.setAttribute('content', originalContent);
      }
    };
  }, [disableZoom, override]);
};

export default useViewportControl;
